var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Vector3, Quaternion } from "three";
import { mainScene } from "@modules/rendering/mainScene";
import { busService } from "@modules/bus/BusService";
import { inputService } from "@modules/inputs/input/InputService";
import { GizmoButtonGroup } from "./GizmoButtonGroup";
import { GizmoVRControllerSupport } from "./GizmoVRControllerSupport";
import { GizmoModelController } from "./GizmoModelController";
export class GizmoProperties {
    constructor(enabled = true, // whether to show or hide the gizmo if set to false all other attributes are ignored
    target = 0, // target modelUID
    position = new Vector3(), rotation = new Quaternion(), scale = new Vector3(), mode = "translate", scalable = true) {
        this.enabled = enabled;
        this.target = target;
        this.position = position;
        this.rotation = rotation;
        this.scale = scale;
        this.mode = mode;
        this.scalable = scalable;
    }
}
/**
 * The GizmoService is doing the actual job of displaying a Gizmo as well as handling THREE.JS events and informing
 * other modules and peers of the position of the Model that is being manipulated.
 *
 * It attaches an invisible cube at
 * the position of the selected model and then applies the translation, rotation and scaling of the invisible
 * cube to the selected model. The updated transform of the cube is then broadcast to other modules, peers and the
 * server to persist the new position. While the Gizmo is active, the Collider facet is not selectable to avoid problems
 * when the Model is bigger than the buttons used to switch between different transform modes. If a user selects
 * a Model that is currently active for another peer, the gizmo of the other peer is deselected to ensure that a Model
 * can only be controlled by one user at a time.
 */
class GizmoService {
    constructor() {
        this.connection = busService.connection("GizmoService");
        this.modelController = new GizmoModelController(this.connection);
        this.modelController.setTransformMode("translate");
        this.modelController.setMinScale(0.001);
        this.modelController.setMaxScale(100);
        this.vrControllerSupport = new GizmoVRControllerSupport(this.modelController.getControls(), this.connection);
        this.vrControllerSupport.onSelectEnd = () => {
            this.modelController.endInteraction();
        };
        this.gizmoButtonGroup = new GizmoButtonGroup(this.modelController.getDummyTarget(), this.connection);
        mainScene.add(this.gizmoButtonGroup.group);
        this.gizmoButtonGroup.hide();
        // Initialize gizmo service state
        this.targetModelUid = -1;
        this.scalable = true;
        // Setup keyboard input
        inputService
            .registerAction("ToggleDebugRayVisibility", () => {
            this.vrControllerSupport.toggleDebugRayVisibility();
        }, "Show and hide debug ray used for selecting the gizmo")
            .addKey("KeyY")
            .asDev();
    }
    updateGizmo(properties) {
        if (!properties.enabled) {
            this.hideGizmo();
            return;
        }
        this.showGizmo(properties);
    }
    peerDisablesGizmo(modelUid) {
        if (modelUid !== this.targetModelUid)
            return;
        this.hideGizmo();
    }
    handleGizmoLockChanged(modelUid) {
        this.modelController.updateSelectableStateForCurrentLock(modelUid);
    }
    /// Gizmo update method
    /**
     * Switches between translate (0), rotation (1) and scale (2)
     * @param gizmoType
     */
    setGizmoType(gizmoType) {
        if (gizmoType > 2)
            return;
        let transformMode = "translate";
        if (gizmoType === 1)
            transformMode = "rotate";
        else if (gizmoType === 2)
            transformMode = "scale";
        this.gizmoButtonGroup.setTransformMode(transformMode);
        this.modelController.setTransformMode(transformMode, true);
    }
    setGizmoCoordinateSpace(isLocal) {
        const space = isLocal ? "local" : "world";
        this.gizmoButtonGroup.setCoordinateSpace(space);
        this.modelController.setCoordinateSpace(space, true);
    }
    setGizmoTransform(modelUid_1, position_1, rotation_1, scale_1) {
        return __awaiter(this, arguments, void 0, function* (modelUid, position, rotation, scale, protectLockedTransformations = false) {
            const currentModelUid = this.modelController.getModelUid();
            if (currentModelUid !== modelUid)
                return;
            const isScalable = this.scalable || !protectLockedTransformations;
            this.modelController.setTransform(position, rotation, scale, isScalable);
        });
    }
    resetScale() {
        this.modelController.resetScale();
    }
    handleModelDeleted(modelUid) {
        if (modelUid !== this.targetModelUid)
            return;
        this.hideGizmo();
    }
    /// Private methods ///
    showGizmo(properties) {
        const modelChanged = this.targetModelUid !== -1 && this.targetModelUid !== properties.target;
        this.targetModelUid = properties.target;
        this.scalable = properties.scalable;
        // Update model controller state
        if (modelChanged)
            this.modelController.update(this.targetModelUid);
        else {
            this.modelController.show(this.targetModelUid);
            this.vrControllerSupport.resetState();
        }
        // Use the transform of the newly selected model
        this.modelController.setTransform(properties.position, properties.rotation, properties.scale, this.scalable);
        // Update the button group based on the newly selected model transform and properties
        if (modelChanged)
            this.gizmoButtonGroup.update(this.scalable);
        else
            this.gizmoButtonGroup.show(this.scalable);
        this.gizmoButtonGroup.setCoordinateSpace("world");
    }
    hideGizmo() {
        this.modelController.hide();
        this.gizmoButtonGroup.hide();
        this.targetModelUid = -1;
    }
}
export const gizmoService = new GizmoService();
